/**
 * Living registry of CSR Workspace mock elements vs VitalData API support.
 * Update whenever an Overview (or later-tab) widget is wired, stubbed, or
 * unblocked by a new API. Statuses: missing | partial | client_heuristic | available
 */

export const OVERVIEW_API_GAPS = [
  {
    id: 'to_consult_pct',
    tab: 'overview',
    element: 'KPI: To Consult %',
    status: 'partial',
    notes: 'No dedicated REST KPI; Referrals tab computes conversion client-side from referral-events + aliases.',
    v1Behavior: 'Omit Overview tile; show on Referrals tab funnel',
  },
  {
    id: 'active_users_denom',
    tab: 'overview',
    element: 'KPI: Active users “of Y” seats',
    status: 'partial',
    notes: 'Distinct login users (X) from cache; no licensed-seat field on Customer.',
    v1Behavior: 'Show “N active users” only',
  },
  {
    id: 'last_touch_crm',
    tab: 'overview',
    element: 'Header: Last touch (HubSpot)',
    status: 'missing',
    notes: 'No HubSpot/CRM touch API.',
    v1Behavior: 'Latest QBR meetingDate if any; else hide pill',
  },
  {
    id: 'hub_paying_badge',
    tab: 'overview',
    element: 'Header: Hub · Paying',
    status: 'missing',
    notes: 'No isHub / isPaying flags on Customer.',
    v1Behavior: 'Show contract/payment context only',
  },
  {
    id: 'health_score_label',
    tab: 'overview',
    element: 'Header: Health score · At risk',
    status: 'partial',
    notes: 'API: green/yellow/red + composite 0–100 in health.signals; not Command Pack 1–10.',
    v1Behavior: 'Map color → label; show composite when parsed',
  },
  {
    id: 'quiet_sites_60d',
    tab: 'overview',
    element: 'Needs attention: quiet referring sites',
    status: 'client_heuristic',
    notes: 'No dedicated REST; client scan of cached referrals, 60+ days.',
    v1Behavior: 'Client heuristic',
  },
  {
    id: 'dormant_users_60d',
    tab: 'overview',
    element: 'Needs attention: dormant hub users',
    status: 'client_heuristic',
    notes: 'No dedicated REST; client scan of cached logins, 60+ days.',
    v1Behavior: 'Client heuristic',
  },
  {
    id: 'vera_insight_blurb',
    tab: 'overview',
    element: 'VERA insight narrative',
    status: 'client_heuristic',
    notes: 'Not calling VERA chat/tools for this blurb.',
    v1Behavior: 'Rule-based sentence from YoY + quiet counts',
  },
  {
    id: 'referrals_yoy',
    tab: 'overview',
    element: 'KPI + monthly chart YoY',
    status: 'available',
    notes: '/metrics previous window + prior-year referrals cache.',
    v1Behavior: 'Ship',
  },
  {
    id: 'logins_msg_studies_yoy',
    tab: 'overview',
    element: 'KPI Logins / Messages / Studies YoY',
    status: 'available',
    notes: 'Same /metrics windows (or cache rollups for filter/facility scope).',
    v1Behavior: 'Ship',
  },
  {
    id: 'csr_contract_facilities',
    tab: 'overview',
    element: 'Header CSR, contract $, facility count',
    status: 'available',
    notes: 'Customer detail + selected facility count.',
    v1Behavior: 'Ship',
  },
  {
    id: 'referrals_funnel',
    tab: 'referrals',
    element: 'Received → consult → procedure funnel',
    status: 'client_heuristic',
    notes: 'Client join of referrals + referral-events; aliases editable in UI.',
    v1Behavior: 'Ship with default aliases + Apply',
  },
  {
    id: 'referrals_internal_toggle',
    tab: 'referrals',
    element: 'Internal referrals include/exclude',
    status: 'available',
    notes: 'creating_facility == receiving_facility.',
    v1Behavior: 'Ship; default Excluded',
  },
  {
    id: 'referrals_source_mix',
    tab: 'referrals',
    element: 'How referrals arrive (Direct / Quick / vFax)',
    status: 'partial',
    notes: 'No dedicated vFax-converted flag.',
    v1Behavior: 'External / non-VE sender bucket',
  },
  {
    id: 'referrals_speed',
    tab: 'referrals',
    element: 'Days referral→consult / consult→procedure',
    status: 'client_heuristic',
    notes: 'Avg/median from Schedule Event join.',
    v1Behavior: 'Ship when events cached',
  },
  {
    id: 'sites_table',
    tab: 'sites',
    element: 'Referring sites table + YoY + senders',
    status: 'client_heuristic',
    notes: 'Rollup of inbound referrals by sending_facility_id.',
    v1Behavior: 'Ship',
  },
  {
    id: 'sites_hubspot',
    tab: 'sites',
    element: 'HubSpot outreach actions',
    status: 'missing',
    notes: 'No HubSpot API.',
    v1Behavior: 'Footnote only; no buttons',
  },
  {
    id: 'sites_activity_cols',
    tab: 'sites',
    element: 'Optional Messages / Logins / Studies columns',
    status: 'partial',
    notes: 'Matched by facility_id = sending site; may undercount if activity is under other facilities.',
    v1Behavior: 'Optional columns',
  },
  {
    id: 'users_adoption',
    tab: 'users',
    element: 'Hub adoption active / dormant',
    status: 'client_heuristic',
    notes: 'From hub-facility logins; active = lastActive < 60d.',
    v1Behavior: 'Ship',
  },
  {
    id: 'users_mfa_seats',
    tab: 'users',
    element: 'MFA % / licensed seats',
    status: 'missing',
    notes: 'No MFA or seat fields in API.',
    v1Behavior: 'Show — for MFA; omit seat denominator',
  },
  {
    id: 'users_trending',
    tab: 'users',
    element: 'Trending up/down people',
    status: 'client_heuristic',
    notes: 'Current vs prior-year window login volume (not strict 90 vs prior 90).',
    v1Behavior: 'Ship',
  },
  {
    id: 'imaging_studies',
    tab: 'imaging',
    element: 'DICOM studies + in/out',
    status: 'available',
    notes: 'studies cache + metrics; in/out from study_sent/received.',
    v1Behavior: 'Ship',
  },
  {
    id: 'imaging_pacs_emr',
    tab: 'imaging',
    element: 'PACS connection / EMR direct push',
    status: 'missing',
    notes: 'No cache types for PACS status or EMR push.',
    v1Behavior: 'Show — tiles',
  },
  {
    id: 'imaging_fax',
    tab: 'imaging',
    element: 'vFax pages / converted',
    status: 'partial',
    notes: 'faxes.num_pages available; “converted” uses non-VE referral proxy.',
    v1Behavior: 'Pages + external-sender proxy',
  },
  {
    id: 'imaging_qr_rounds',
    tab: 'imaging',
    element: 'Quick Refer count / VitalRounds',
    status: 'partial',
    notes: 'QR = referral_type 3 count (not link inventory). VitalRounds from cache/metrics.',
    v1Behavior: 'Ship',
  },
  {
    id: 'touch_qbr',
    tab: 'touch',
    element: 'Cadence / QBR timeline / CSR contacts',
    status: 'partial',
    notes: 'QBRs + contract + CSR names on customer; no HubSpot timeline/tasks.',
    v1Behavior: 'Ship for customer scope only',
  },
  {
    id: 'touch_hubspot',
    tab: 'touch',
    element: 'HubSpot timeline / key contacts CRM',
    status: 'missing',
    notes: 'No HubSpot integration.',
    v1Behavior: 'Footnotes; CSR names from VitalData only',
  },
  {
    id: 'reports_qbr',
    tab: 'reports',
    element: 'QBR block picker / generate',
    status: 'missing',
    notes: 'Tab intentionally not built; use Agentic Reporting.',
    v1Behavior: 'Placeholder',
  },
];

export function gapsNeedingAttention() {
  return OVERVIEW_API_GAPS.filter(g => g.status !== 'available');
}
